import React, { memo } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type {
  HourInterval,
  IntervalPattern,
  ReservationSlotTemplate,
} from '../../../../domain/reservationSlots';
import { countSlotsPerHour, expandTemplateToHour } from '../../../../domain/reservationSlots';
import { formatTemplateSpec } from '../formatTemplateLabel';
import {
  TemplateBand,
  TemplateBandRow,
  TemplateCardRoot,
} from '../ReservationSlotManagement.styles';
import CategoryChip from './CategoryChip';

/**
 * テンプレートカードに表示するバッジ種別
 * - default:  基本設定（デフォルトテンプレート）として割り当てられている
 * - override: 医師個別の上書きテンプレートとして使われている
 */
export type TemplateCardBadge = 'default' | 'override';

/**
 * TemplateCard のProps型
 *
 * @property {ReservationSlotTemplate} template - 表示対象のテンプレート
 * @property {TemplateCardBadge | null} [badge] - 右上に表示するバッジ種別。null/未指定でバッジなし
 * @property {function} [onEdit] - 「編集」ボタン押下時のコールバック。未指定なら編集ボタンを表示しない
 */
export interface TemplateCardProps {
  template: ReservationSlotTemplate;
  badge?: TemplateCardBadge | null;
  onEdit?: (template: ReservationSlotTemplate) => void;
}

/**
 * バッジ種別ごとの表示ラベル
 */
const BADGE_LABELS: Record<TemplateCardBadge, string> = {
  default: '基本設定',
  override: '個別上書き',
};

/**
 * 区間の分オフセットを「:00」形式の表示文字列にする
 *
 * @description
 * 2桁ゼロ埋めで表示する。1時間の終端（60分）は次の時の開始として「:00」と表す。
 *
 * @param {number} minute - 時の開始からの経過分（0〜60）
 * @returns {string} 「:15」形式の文字列
 */
function formatMinuteMark(minute: number): string {
  return `:${String(minute % 60).padStart(2, '0')}`;
}

/**
 * 区間の時間帯ラベルを組み立てる
 *
 * @param {HourInterval} interval - 対象区間
 * @returns {string} 「:00–:15」形式の文字列
 */
function formatIntervalRange(interval: HourInterval): string {
  return `${formatMinuteMark(interval.startMinute)}–${formatMinuteMark(interval.endMinute)}`;
}

/**
 * 解放枠テンプレートのカード表示
 *
 * @description
 * テンプレート名・仕様・1時間分の区間バンド・1時間あたりのスロット数をカード形式で表示する。
 * 区間バンドは `expandTemplateToHour` の結果を横並びにし、交互パターンの場合のみA/Bラベルを付ける。
 * バッジが付いているテンプレート（基本設定・個別上書き）は、現在割り当てられている
 * テンプレートであることを示すため「選択中」チップを添えてカード枠を強調する。
 *
 * @param {TemplateCardProps} props - コンポーネントProps
 * @returns {React.ReactElement} テンプレートカード
 */
const TemplateCard: React.FC<TemplateCardProps> = memo(({ template, badge, onEdit }) => {
  const theme = useTheme();
  const intervals = expandTemplateToHour(template);
  const slotsPerHour = countSlotsPerHour(template);

  const badgeColors =
    badge === 'override'
      ? {
          bg: alpha(theme.palette.warning.main, 0.16),
          fg: theme.palette.warning.dark,
        }
      : {
          bg: alpha(theme.palette.primary.main, 0.12),
          fg: theme.palette.primary.dark,
        };

  const patternColors: Record<IntervalPattern, { bg: string; fg: string }> = {
    A: { bg: alpha(theme.palette.primary.main, 0.12), fg: theme.palette.primary.dark },
    B: { bg: alpha(theme.palette.success.main, 0.12), fg: theme.palette.success.dark },
  };

  const hasHeaderAction = badge != null || onEdit !== undefined;

  return (
    <TemplateCardRoot $selected={badge != null}>
      {/* ヘッダー行：テンプレート名 / バッジ・編集ボタン */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          mb: 0.5,
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: 14, minWidth: 0 }}>{template.name}</Typography>
        {hasHeaderAction && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
            {/* 現在どこかに割り当てられているテンプレートであることを示す */}
            {badge != null && (
              <Chip
                label="選択中"
                size="small"
                variant="outlined"
                sx={{
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.dark,
                  fontWeight: 800,
                  borderRadius: 1,
                }}
              />
            )}
            {badge != null && (
              <Chip
                label={BADGE_LABELS[badge]}
                size="small"
                sx={{
                  backgroundColor: badgeColors.bg,
                  color: badgeColors.fg,
                  fontWeight: 800,
                  borderRadius: 1,
                }}
              />
            )}
            {onEdit !== undefined && (
              <Button size="small" variant="outlined" onClick={() => onEdit(template)}>
                編集
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* 副題：テンプレートの仕様 */}
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}>
        {formatTemplateSpec(template)}
      </Typography>

      {/* 1時間分の区間バンド */}
      <TemplateBandRow>
        {intervals.map((interval) => (
          <TemplateBand
            key={interval.index}
            sx={{ '& .MuiChip-root': { width: '100%' } }}
          >
            {interval.pattern !== null && (
              <Box
                sx={{
                  backgroundColor: patternColors[interval.pattern].bg,
                  color: patternColors[interval.pattern].fg,
                  borderRadius: 0.5,
                  fontSize: 9,
                  fontWeight: 800,
                  textAlign: 'center',
                  py: 0.125,
                }}
              >
                {interval.pattern}
              </Box>
            )}
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontSize: 9,
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              {formatIntervalRange(interval)}
            </Typography>
            {interval.slots.map((category, slotIndex) => (
              <CategoryChip
                // 同一区間内に同じカテゴリが複数並ぶため、位置をキーに含める
                key={`${interval.index}-${slotIndex}-${category}`}
                category={category}
              />
            ))}
          </TemplateBand>
        ))}
      </TemplateBandRow>

      {/* フッター：1時間あたりのスロット数 */}
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
        1時間あたり{' '}
        <Box component="strong" sx={{ color: 'text.primary', fontWeight: 700 }}>
          {slotsPerHour}スロット
        </Box>
      </Typography>
    </TemplateCardRoot>
  );
});

TemplateCard.displayName = 'TemplateCard';

export default TemplateCard;
